import React from 'react';
import { TrendingUp, CheckCircle, Target, BarChart3, Zap, Shield, Globe } from 'lucide-react';

export default function OnlineSales() {
  const marketplaces = [
    { name: 'Amazon.com', logo: 'https://logo.clearbit.com/amazon.com', description: 'US Marketplace' },
    { name: 'Walmart.com', logo: 'https://logo.clearbit.com/walmart.com', description: 'US Retail' },
    { name: 'Etsy', logo: 'https://logo.clearbit.com/etsy.com', description: 'Handmade & Unique' },
    { name: 'eBay', logo: 'https://logo.clearbit.com/ebay.com', description: 'Global Marketplace' },
    { name: 'Mercado Libre', logo: 'https://logo.clearbit.com/mercadolibre.com', description: 'Latin America' },
    { name: 'Amazon.ca', logo: 'https://logo.clearbit.com/amazon.ca', description: 'Canada' }
  ];

  const framework = [
    {
      phase: 'Phase 1: Marketplace Strategy & Selection',
      duration: '1-2 weeks',
      activities: [
        'Product-marketplace fit analysis across 20+ platforms',
        'Competitive landscape assessment and white space identification',
        'Pricing strategy development for each marketplace',
        'Fulfillment model selection (FBA, 3PL, direct ship)',
        'Launch timeline and resource allocation'
      ]
    },
    {
      phase: 'Phase 2: Listing Optimization',
      duration: '2-3 weeks',
      activities: [
        'SEO keyword research and title optimization',
        'A+ content creation with conversion-focused copywriting',
        'Professional product photography and lifestyle imagery',
        'Video content production for enhanced listings',
        'Backend search term optimization'
      ]
    },
    {
      phase: 'Phase 3: Launch & Traffic Acquisition',
      duration: '4-6 weeks',
      activities: [
        'Strategic product launches with velocity building',
        'PPC campaign setup and optimization across platforms',
        'Promotional strategy (deals, coupons, lightning deals)',
        'External traffic driving (social, influencer, email)',
        'Review acquisition campaigns (compliant strategies)'
      ]
    },
    {
      phase: 'Phase 4: Growth & Optimization',
      duration: 'Ongoing',
      activities: [
        'Daily PPC bid management and campaign optimization',
        'Inventory forecasting and restock management',
        'Listing performance analysis and A/B testing',
        'Review monitoring and customer service excellence',
        'Account health maintenance and issue resolution'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'The Flywheel Method',
      icon: Zap,
      description: 'We build marketplace momentum through coordinated launches, aggressive PPC, external traffic, and review velocity—creating self-sustaining organic ranking growth within 90 days.'
    },
    {
      title: 'Profit-First PPC',
      icon: Target,
      description: 'Most agencies optimize for revenue. We optimize for profit. Our sophisticated bid strategies maintain 15-25% ACoS while scaling, not the 40%+ industry average that kills margins.'
    },
    {
      title: 'Multi-Platform Mastery',
      icon: Globe,
      description: 'Amazon is just the beginning. We help brands diversify across Walmart, Target+, eBay, Etsy, and international marketplaces—reducing platform risk and maximizing total addressable market.'
    },
    {
      title: 'Account Protection',
      icon: Shield,
      description: 'Marketplace account suspensions can kill brands overnight. Our compliance-first approach and issue resolution expertise protects your account health and revenue continuity.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <TrendingUp size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                Online Sales Acceleration
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Accelerate your e-commerce growth
              </p>
            </div>
          </div>
        </div>

        {/* Marketplaces We Excel In */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Marketplaces We Excel In</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <p className="text-lg text-gray-700 mb-8">
              We help brands launch and scale across major e-commerce marketplaces, leveraging platform-specific strategies and deep expertise in conversion optimization, advertising, and growth tactics.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {marketplaces.map((marketplace, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#38A3A5] transition-all">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 h-16 flex items-center justify-center">
                      <img 
                        src={marketplace.logo} 
                        alt={marketplace.name}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{display: 'none'}} className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <TrendingUp size={24} className="text-gray-400" />
                      </div>
                    </div>
                    <h3 className="font-bold text-sm text-gray-800 mb-1">{marketplace.name}</h3>
                    <p className="text-xs text-gray-600">{marketplace.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Marketplace Acceleration Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Launching on marketplaces is easy. Scaling profitably is hard. Most brands plateau at $20-50K/month because they treat marketplaces as passive sales channels instead of sophisticated performance marketing platforms. We approach marketplace growth systematically:
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Most marketplace agencies are order-takers who set up listings and run basic PPC. We're growth partners who build sophisticated systems for sustainable, profitable scaling across multiple platforms.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Results Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Proven Results</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">3.2x</div>
              <div className="text-lg opacity-90">Average revenue growth in first 12 months</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">18%</div>
              <div className="text-lg opacity-90">Average ACoS (advertising cost of sales)</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">$1.8M</div>
              <div className="text-lg opacity-90">Average annual marketplace revenue per brand</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Accelerate Your Marketplace Growth?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss your current marketplace performance and build a strategic growth plan.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
